import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/* ─────────────────────  M3 card primitives  ─────────────────────
 * Shared between AppShell (Today view) and the individual module
 * views (FamStatus, FamList, FamTree, FamilyChat). FamMood folded
 * into FamStatus on merge — its surfaces use the same primitives.
 *
 * Kept minimal on purpose — module views add their own motion with
 * Stagger / StaggerItem from FadeIn.
 */

export function M3Card({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "rounded-2xl border border-border/60 bg-card p-4 shadow-sm",
        className,
      )}
    >
      {children}
    </section>
  );
}

export function M3CardHeader({
  title,
  subtitle,
  trailing,
  trailingHref,
  icon,
}: {
  title: string;
  subtitle?: string;
  // String-only was the original contract, but at least one caller
  // (FamStatusView's mood-card "記錄心情" trailing button) needs a
  // ReactNode. Earlier inspects of grep showed other JSX-looking
  // matches but those turned out to be `AppBar`/`Header`, not this
  // component. Widening to ReactNode keeps strings typed as strings
  // (assignable to ReactNode) while letting the mood button in.
  trailing?: ReactNode;
  trailingHref?: string;
  icon?: ReactNode;
}) {
  return (
    <header className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-2">
        {icon && (
          <span
            aria-hidden
            className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"
          >
            {icon}
          </span>
        )}
        <div>
          <h2 className="font-serif text-base font-semibold leading-tight sm:text-lg">
            {title}
          </h2>
          {subtitle && (
            <p className="text-[11px] text-muted-foreground">{subtitle}</p>
          )}
        </div>
      </div>
      {trailing &&
        (trailingHref ? (
          <a
            href={trailingHref}
            className="shrink-0 text-xs font-medium text-primary hover:underline"
          >
            {trailing}
          </a>
        ) : (
          <span className="shrink-0 text-[11px] text-muted-foreground">
            {trailing}
          </span>
        ))}
    </header>
  );
}
